import { useState, useRef, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { formatISO, parse, addMinutes } from 'date-fns';
import api from '../../api/client';
import { TimeSelect } from '../common/DateTimePicker';
import { UploadProgress, UploadProgressList } from '../common/UploadProgress';

const AUDIO_ACCEPT = 'audio/*,.wav,.mp3,.aac,.flac,.ogg,.m4a,.wma';

export default function EpisodeUpload() {
  const { id: showId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    episode_number: '',
    scheduled_for: '',
    description: '',
    file: null,
  });

  const [uploads, setUploads] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [dragTime, setDragTime] = useState('');

  const handleDragOver = (e) => {
    e.preventDefault();
    e.persist && e.persist();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!AUDIO_ACCEPT.includes(file.type) && !file.name.match(/\.(mp3|wav|aac|flac|ogg|m4a|wma)$/i)) {
      setErrors({ file: 'Seleccioná un archivo de audio válido.' });
      return;
    }
    setFormData((prev) => ({ ...prev, file }));
    setErrors((prev) => ({ ...prev, file: undefined }));

    const uploadId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();
    setUploads((prev) => [...prev, { id: uploadId, file, progress: 0, status: 'uploading' }]);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const buildFormData = () => {
    const fd = new FormData();
    fd.append('title', formData.title);
    fd.append('episode_number', formData.episode_number);
    fd.append('description', formData.description);
    if (formData.scheduled_for) {
      fd.append('scheduled_for', formData.scheduled_for);
    }
    if (formData.file) {
      fd.append('file', formData.file);
    }
    return fd;
  };

  const simulateProgress = (uploadId) => {
    const interval = setInterval(() => {
      setUploads((prev) =>
        prev.map((u) => {
          if (u.id !== uploadId) return u;
          const inc = Math.floor(Math.random() * 10) + 5;
          const newProgress = Math.min(u.progress + inc, 85);
          return { ...u, progress: newProgress, status: 'processing' };
        })
      );
    }, 500);
    return interval;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.file) {
      setErrors({ file: 'Seleccioná un archivo de audio para subir.' });
      return;
    }
    if (!formData.title.trim()) {
      setErrors({ title: 'El título es obligatorio' });
      return;
    }

    setIsSubmitting(true);
    const uploadId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString();
    setUploads((prev) => [...prev, { id: uploadId, file: formData.file, progress: 0, status: 'uploading' }]);

    const progressInterval = simulateProgress(uploadId);

    try {
      const fd = buildFormData();
      if (showId) {
        fd.append('show_id', showId);
      }

      const { data } = await api.post('/api/episodes/upload', fd, {
        onUploadProgress: (prog) => {
          const percent = Math.round((prog.event.loaded / prog.event.total) * 100);
          setUploads((prev) =>
            prev.map((u) => (u.id === uploadId ? { ...u, progress: percent, status: 'uploading' } : u))
          );
        },
      });

      clearInterval(progressInterval);
      setUploads((prev) =>
        prev.map((u) => (u.id === uploadId ? { ...u, progress: 100, status: 'ready' } : u))
      );
      setTimeout(() => {
        setUploads((prev) => prev.filter((u) => u.id !== uploadId));
      }, 2000);

      const dest = showId ? `/shows/${showId}/episodes` : '/episodes';
      navigate(dest);
    } catch (err) {
      clearInterval(progressInterval);
      console.error('[EpisodeUpload] Upload failed:', err);
      setUploads((prev) =>
        prev.map((u) => (u.id === uploadId ? { ...u, status: 'error' } : u))
      );
      setErrors({ submit: 'Error al subir. Intentá de nuevo.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-dark-100">
          {showId ? 'Subir Episodio' : 'Subir Nuevo Episodio'}
        </h1>
        <Link to={showId ? `/shows/${showId}/episodes` : '/episodes'} className="btn btn-secondary btn-sm">
          Volver
        </Link>
      </div>

      {errors.submit && (
        <div className="rounded-lg bg-error-900/30 border border-error-700 p-3 text-sm text-error-400">
          {errors.submit}
        </div>
      )}

      <form onSubmit={handleSubmit} id="episode-upload-form" className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* File uploader */}
        <div className="space-y-4">
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={`relative cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-all ${
              isDragging
                ? 'border-primary-500 bg-dark-800/50'
                : errors.file
                ? 'border-error-500'
                : 'border-dark-700 hover:border-dark-600'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={AUDIO_ACCEPT}
              onChange={(e) => handleFileSelect(e.target.files?.[0])}
              className="hidden"
            />
            {formData.file ? (
              <div className="space-y-2">
                <span className="text-3xl">🎧</span>
                <p className="text-sm font-medium text-dark-100 break-all">{formData.file.name}</p>
                <p className="text-xs text-dark-500">
                  {(formData.file.size / (1024 * 1024)).toFixed(2)} MB
                </p>
              </div>
            ) : (
              <>
                <span className="text-4xl mb-3 block">🎵</span>
                <p className="text-sm font-medium text-dark-100">Arrastrá el archivo de audio o hacé clic para explorar</p>
                <p className="mt-2 text-xs text-dark-500">Soporta: MP3, WAV, AAC, FLAC, OGG, M4A</p>
              </>
            )}
            {errors.file && <p className="mt-2 text-xs text-error-400">{errors.file}</p>}
          </div>

          {uploads.length > 0 && (
            <div className="space-y-2">
              <UploadProgressList uploads={uploads} />
            </div>
          )}
        </div>

        {/* Form details */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Título *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={`input ${errors.title ? 'border-error-500' : ''}`}
              placeholder="Título del episodio"
            />
            {errors.title && <p className="text-xs text-error-400 mt-1">{errors.title}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Número de episodio</label>
            <input
              type="number"
              name="episode_number"
              value={formData.episode_number}
              onChange={handleChange}
              className="input"
              placeholder="Ej: 42"
              min="1"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Fecha programada</label>
            <input
              type="date"
              name="scheduled_for_date"
              value={formData.scheduled_for ? formData.scheduled_for.split('T')[0] : ''}
              onChange={(e) => {
                const date = e.target.value;
                const time = formData.scheduled_for ? formData.scheduled_for.split('T')[1] : '12:00';
                setFormData((prev) => ({ ...prev, scheduled_for: `${date}T${time}` }));
              }}
              className="input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Hora programada</label>
            <TimeSelect
              value={formData.scheduled_for ? formData.scheduled_for.split('T')[1]?.slice(0, 5) : '12:00'}
              onChange={(time) => {
                const date = formData.scheduled_for ? formData.scheduled_for.split('T')[0] : new Date().toISOString().split('T')[0];
                setFormData((prev) => ({ ...prev, scheduled_for: `${date}T${time}` }));
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-dark-300 mb-1">Descripción</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className="input min-h-[100px]"
              placeholder="Descripción o notas del episodio..."
            />
          </div>
        </div>
      </form>

      <div className="flex justify-end gap-3 border-t border-dark-700 pt-6">
        <Link to={showId ? `/shows/${showId}/episodes` : '/episodes'} className="btn btn-secondary">
          Cancelar
        </Link>
        <button type="submit" form="episode-upload-form" disabled={isSubmitting || !formData.file} className="btn btn-primary">
          {isSubmitting ? 'Subiendo...' : 'Subir Episodio'}
        </button>
      </div>
    </div>
  );
}
